import React from "react";
import { Link } from "react-router-dom";
import { FiArrowDown, FiArrowUp, FiPlus } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import standardTreatmentService from "services/StandardTreatment";
import useServiceData from "pages/StandardTreatment/shared/useServiceData";
import { THERAPY_OPERATORS, formatBandDose, formatBandRange, pickValue, toOption } from "../constants";
import { getTherapyErrors } from "../validation";

const newTherapy = () => ({
  id: `new-${Date.now()}`,
  drug_id: "",
  operator: "DOSE_BAND",
  regimen_id: null,
  exclusive_group: "",
});

// Dosis standar aturan dosis terpilih: GET /regimen/{id} → regimen_dose_bands (read-only).
const RegimenBands = ({ regimenId }) => {
  const { data, loading, error } = useServiceData(() => standardTreatmentService.getRegimen(regimenId), [regimenId]);
  const bands = data?.regimen_dose_bands || [];
  return (
    <div className="mps-dose-bands">
      <p className="mps-dose-title">
        Dosis mengikuti dosis standar <strong>{data?.code || "…"}</strong> ·{" "}
        <Link to={`/dosis-standar?regimen_id=${regimenId}`}>kelola</Link>
      </p>
      {loading && !data && <p className="mps-helper mb-0">Memuat dosis standar...</p>}
      {error && <p className="mps-helper is-error mb-0">{error.join(", ")}</p>}
      {data && !bands.length && (
        <p className="mps-helper is-error mb-0">Aturan dosis ini belum punya dosis standar (REGIMEN_HAS_DOSE_BAND).</p>
      )}
      {bands.length > 0 && (
        <dl>
          {bands.map((b) => (
            <div key={b.id}>
              <dt>{formatBandRange(b)}</dt>
              <dd>{formatBandDose(b)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

// Terapi satu Rule (Therapy[] dari RuleDetail.rule_therapies). Obat & aturan dosis dari master data.
const TherapyDoseStep = ({ therapies, onChange, readOnly, showErrors, masters }) => {
  const { drugs, regimens } = masters;
  const drugById = (id) => drugs.find((d) => d.id === id);
  // Pilihan baru hanya master aktif; master nonaktif yang sudah terpakai tetap ditampilkan.
  const drugOptions = (currentId) =>
    drugs
      .filter((d) => d.is_active || d.id === currentId)
      .map((d) => ({ label: `${d.name} (${d.code})${d.is_active ? "" : " — nonaktif"}`, value: d.id }));
  const regimenOptions = (drugId, currentId) =>
    regimens
      .filter((r) => r.drug_id === drugId && (r.is_active || r.id === currentId))
      .map((r) => ({ label: `${r.name} (${r.code})${r.is_active ? "" : " — nonaktif"}`, value: r.id }));

  const update = (index, patch) => onChange(therapies.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  const move = (index, delta) => {
    const next = [...therapies];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    onChange(next);
  };

  return (
    <>
      <p className="mps-helper mb-3">
        Obat & aturan dosis dari <Link to="/master-obat">Master Obat</Link>. Urutan ={" "}
        <code>sequence</code>. Obat di luar daftar terapi membuat kasus dinilai Tidak Standard.
      </p>

      {therapies.length ? (
        <ol className="mps-list">
          {therapies.map((t, i) => {
            const errors = getTherapyErrors(t, therapies);
            const errorOf = (key) => !readOnly && (showErrors || t.drug_id) && errors[key];
            const drug = drugById(t.drug_id) || t.drug;
            const regOptions = regimenOptions(t.drug_id, t.regimen_id);
            const isDoseBand = t.operator === "DOSE_BAND";

            return (
              <li key={t.id || i} className="mps-therapy-card">
                <div className="mps-therapy-order">
                  <span className="mps-order-badge" title="sequence">
                    {i + 1}
                  </span>
                  {!readOnly && (
                    <>
                      <button type="button" className="mps-icon-btn" aria-label={`Naikkan terapi ${i + 1}`} disabled={i === 0} onClick={() => move(i, -1)}>
                        <FiArrowUp size={14} />
                      </button>
                      <button type="button" className="mps-icon-btn" aria-label={`Turunkan terapi ${i + 1}`} disabled={i === therapies.length - 1} onClick={() => move(i, 1)}>
                        <FiArrowDown size={14} />
                      </button>
                    </>
                  )}
                </div>

                <div className="mps-therapy-body">
                  <div className="mps-therapy-head">
                    <h4 className="mps-therapy-name">Terapi #{i + 1}</h4>
                    {!readOnly && <ActionButton btnType="delete" onClick={() => onChange(therapies.filter((_, idx) => idx !== i))} />}
                  </div>

                  <div className="row">
                    <div className="col-md-6 mps-field">
                      <InputSelect
                        label="Obat"
                        isRequired
                        isClearable={false}
                        disabled={readOnly}
                        placeholder="Pilih dari Master Obat"
                        data={drugOptions(t.drug_id)}
                        value={toOption(drugOptions(t.drug_id), t.drug_id)}
                        onChange={(sel) => update(i, { drug_id: pickValue(sel), regimen_id: null })}
                        error={errorOf("drug_id")}
                        errorMessage={errors.drug_id}
                      />
                      {drug && (
                        <small className="mps-helper">
                          Medication key:{" "}
                          {drug.medication_key ? <code className="mps-code">{drug.medication_key}</code> : <strong className="text-danger">belum dipetakan</strong>}
                        </small>
                      )}
                    </div>
                    <div className="col-md-6 mps-field">
                      <InputSelect
                        label="Operator"
                        isRequired
                        isClearable={false}
                        disabled={readOnly}
                        data={THERAPY_OPERATORS}
                        value={toOption(THERAPY_OPERATORS, t.operator)}
                        onChange={(sel) => {
                          const operator = pickValue(sel);
                          update(i, operator === "PRESENT" ? { operator, regimen_id: null } : { operator, exclusive_group: "" });
                        }}
                        error={errorOf("operator")}
                        errorMessage={errors.operator}
                      />
                    </div>
                    <div className="col-md-6 mps-field">
                      {isDoseBand ? (
                        <>
                          <InputSelect
                            label="Aturan Dosis"
                            isRequired
                            isClearable={false}
                            disabled={readOnly || !t.drug_id}
                            placeholder={t.drug_id ? "Pilih aturan dosis" : "Pilih obat dahulu"}
                            data={regOptions}
                            value={toOption(regOptions, t.regimen_id)}
                            onChange={(sel) => update(i, { regimen_id: pickValue(sel) || null })}
                            error={errorOf("regimen_id")}
                            errorMessage={errors.regimen_id}
                          />
                          {!readOnly && t.drug_id && !regOptions.length && (
                            <small className="mps-helper is-error">
                              Belum ada aturan dosis aktif untuk obat ini — tambahkan di{" "}
                              <Link to="/master-obat">Master Obat</Link>.
                            </small>
                          )}
                        </>
                      ) : (
                        <InputField label="Aturan Dosis" disabled value="Tidak diperlukan (PRESENT → null)" />
                      )}
                    </div>
                    <div className="col-md-6 mps-field">
                      <InputField
                        label="Exclusive group"
                        disabled={readOnly || isDoseBand}
                        placeholder={isDoseBand ? "Hanya untuk PRESENT" : "Contoh: DOKSI_TETRA"}
                        maxLengthVal={20}
                        value={t.exclusive_group || ""}
                        onChange={(e) => update(i, { exclusive_group: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
                        error={errorOf("exclusive_group")}
                        errorMessage={errors.exclusive_group}
                      />
                      {!isDoseBand && !readOnly && (
                        <small className="mps-helper">
                          Opsional. Label sama pada ≥ 2 terapi PRESENT = tepat satu obat harus diberikan.
                        </small>
                      )}
                    </div>
                  </div>

                  {isDoseBand && t.regimen_id && <RegimenBands regimenId={t.regimen_id} />}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className={`mps-empty ${showErrors ? "is-error" : ""}`}>
          Rule ini belum punya terapi{readOnly ? "." : ". Tambahkan minimal satu terapi."}
        </div>
      )}

      {!readOnly && (
        <div className="mps-actions-start mt-3">
          <Button type="button" variant="secondary" size="normal" onClick={() => onChange([...therapies, newTherapy()])}>
            <FiPlus size={16} className="mr-2" />
            Tambah Terapi
          </Button>
        </div>
      )}
    </>
  );
};

export default TherapyDoseStep;
